import React from "react";
import "../../../AllComponentCSS.css";

export default function JS_Loops() {
  return (
    <div className="tutorial-container read-only">
      <h1 className="tutorial-title">🔁 JavaScript Loops & Iteration</h1>

      {/* Introduction */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 What are Loops?</h2>
        <p>
          Loops in JavaScript are constructs that allow you to repeat a block of
          code multiple times. They help automate repetitive tasks and iterate
          over collections of data efficiently.
        </p>
        <p>
          At <b>Edu-Tech Master Academy</b>, understanding loops is essential
          for building dynamic and efficient applications.
        </p>
      </div>

      {/* Why Loops */}
      <div className="tutorial-section">
        <h2 className="section-title">🧠 Why use Loops?</h2>
        <ul className="tutorial-list-points">
          <li>
            Automate repetitive tasks without writing the same code multiple
            times.
          </li>
          <li>Iterate over arrays, objects, or other data collections.</li>
          <li>
            Perform tasks based on dynamic conditions (e.g., until a user logs
            in, or until data is processed).
          </li>
          <li>
            Combine with functions, iterables, and generators for advanced
            programming.
          </li>
        </ul>
      </div>

      {/* Types of Loops */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Types of Loops in JavaScript</h2>
        <ul className="tutorial-list-points">
          <li>
            <b>for</b> – Repeats a block a fixed number of times.
          </li>
          <li>
            <b>while</b> – Repeats a block while a condition is true.
          </li>
          <li>
            <b>do...while</b> – Executes a block at least once, then continues
            while a condition is true.
          </li>
          <li>
            <b>for...in</b> – Iterates over the keys of an object.
          </li>
          <li>
            <b>for...of</b> – Iterates over iterable objects like arrays,
            strings, maps, sets.
          </li>
          <li>
            <b>Iterables & Iterators</b> – Advanced ways to traverse data
            structures.
          </li>
          <li>
            <b>Generators</b> – Functions that can pause and resume execution,
            producing multiple values on demand.
          </li>
        </ul>
      </div>

      {/* When to Use Loops */}
      <div className="tutorial-section">
        <h2 className="section-title">⚙️ When to Use Loops</h2>
        <ul className="tutorial-list-points">
          <li>Processing every item in an array or object.</li>
          <li>Performing tasks until a condition is met.</li>
          <li>Generating sequences or repeated content in applications.</li>
          <li>Working with dynamic or unknown-length data sets.</li>
        </ul>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Avoid infinite loops by ensuring the exit condition is reachable.
          </li>
          <li>
            Choose the loop type that best fits the task (<code>for</code> for
            fixed iterations, <code>while</code> for condition-based).
          </li>
          <li>
            Use <code>for...of</code> for readable iteration over arrays and
            iterables.
          </li>
          <li>
            Use iterators or generators for large datasets to improve memory
            efficiency.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fde68a, #fcd34d)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn each loop type individually: <code>while</code>,{" "}
            <code>for</code>, <code>for...in</code>, <code>for...of</code>.
          </li>
          <li>Understand iterables and iterators for advanced traversal.</li>
          <li>Explore generators for producing values on demand.</li>
          <li>
            Practice loops with real-world examples at Edu-Tech Master Academy.
          </li>
        </ul>
      </div>
    </div>
  );
}
